import { createContext, use, useCallback, useEffect, useMemo, useRef } from "react";
import type { RefObject } from "react";

/** Returns whether the current page holds input the person has not saved yet. */
export type UnsavedInputProbe = () => boolean;

/**
 * Holds a draft kept outside form fields as unsaved input (F-104); returns the function that
 * releases it again.
 */
export type UnsavedDraftHold = () => () => void;

/** What the shell offers the current page about unsaved input (F-78, F-104). */
export interface UnsavedInputShell {
  /** Whether the current page holds input not saved yet. */
  readonly probe: UnsavedInputProbe;
  /** Holds a draft that no field shows, for example changes prepared by buttons. */
  readonly holdDraft: UnsavedDraftHold;
}

/**
 * Returns whether an event target is a field a person types into or chooses in.
 *
 * @param target - The target of an `input` or `change` event.
 * @returns `true` for inputs, text areas, selects and editable content.
 */
function isEntryField(target: EventTarget | null): target is HTMLElement {
  const field =
    target instanceof HTMLInputElement ||
    target instanceof HTMLTextAreaElement ||
    target instanceof HTMLSelectElement ||
    (target instanceof HTMLElement && target.isContentEditable);

  return field;
}

/**
 * Follows the input of the person inside a content area (F-78): every field changed by the person
 * counts as unsaved while it is still shown in that area. A form that closes after saving removes its
 * fields, so they no longer count; a field that stays (a form cleared after saving, a filter) still
 * counts, which only means one question too many. Fields outside the area (the panel of the compact
 * clock, the app bar) never count. A draft that no field shows counts while a page holds it through
 * {@link useUnsavedDraft} (F-104).
 *
 * @param areaRef - The content area, for example the main landmark of the shell.
 * @returns The probe and the hold of drafts; stable for the life of the component.
 */
export function useUnsavedInputTracking(areaRef: RefObject<HTMLElement | null>): UnsavedInputShell {
  const changed = useRef(new Set<HTMLElement>());
  const heldDrafts = useRef(0);

  useEffect(() => {
    const area = areaRef.current;

    if (area === null) {
      return undefined;
    }

    const remember = (event: Event): void => {
      if (isEntryField(event.target)) {
        changed.current.add(event.target);
      }
    };
    area.addEventListener("input", remember, true);
    area.addEventListener("change", remember, true);
    const stop = (): void => {
      area.removeEventListener("input", remember, true);
      area.removeEventListener("change", remember, true);
    };

    return stop;
  }, [areaRef]);

  const probe = useCallback((): boolean => {
    const area = areaRef.current;
    const fields = changed.current;

    for (const field of [...fields]) {
      if (!field.isConnected || area?.contains(field) !== true) {
        fields.delete(field);
      }
    }

    const unsaved = fields.size > 0 || heldDrafts.current > 0;

    return unsaved;
  }, [areaRef]);

  const holdDraft = useCallback((): (() => void) => {
    let held = true;
    heldDrafts.current += 1;
    const release = (): void => {
      if (held) {
        held = false;
        heldDrafts.current -= 1;
      }
    };

    return release;
  }, []);

  const shell = useMemo<UnsavedInputShell>(() => ({ probe, holdDraft }), [probe, holdDraft]);

  return shell;
}

/** Never reports unsaved input and holds nothing: outside a shell that follows it. */
const outsideShell: UnsavedInputShell = {
  probe: () => false,
  holdDraft: () => () => undefined,
};

/** Unsaved input of the shell around the current page (F-78, F-104). */
export const UnsavedInputContext = createContext<UnsavedInputShell>(outsideShell);

/**
 * Returns the probe of the shell: whether the current page holds input not saved yet.
 *
 * @returns The probe; outside a shell it always answers `false`.
 */
export function useUnsavedInput(): UnsavedInputProbe {
  const { probe } = use(UnsavedInputContext);

  return probe;
}

/**
 * Counts a draft that no form field shows as unsaved input while `unsaved` is `true` and the
 * component is shown (F-104), for example changes prepared by buttons. Fields changed by the person
 * count on their own.
 *
 * @param unsaved - Whether the component holds such a draft now.
 */
export function useUnsavedDraft(unsaved: boolean): void {
  const { holdDraft } = use(UnsavedInputContext);

  useEffect(() => {
    if (!unsaved) {
      return undefined;
    }

    const release = holdDraft();

    return release;
  }, [unsaved, holdDraft]);
}
